import type { ReportConfig, BuilderState } from '@/types/custom-reports';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Star } from 'lucide-react';

interface Step3VisualizationProps {
  config: ReportConfig;
  reportName: string;
  reportDescription: string;
  isFavorite: boolean;
  isPublic: boolean;
  updateConfig: (updates: Partial<ReportConfig>) => void;
  updateState: (updates: Partial<BuilderState>) => void;
}

export function Step3Visualization({
  config,
  reportName,
  reportDescription,
  isFavorite,
  isPublic,
  updateConfig,
  updateState,
}: Step3VisualizationProps) {
  const visualizationOptions = [
    { id: 'table', label: 'Tabela', icon: '📋', description: 'Dados em formato de tabela' },
    { id: 'bar', label: 'Barras', icon: '📊', description: 'Gráfico de barras comparativo' },
    { id: 'line', label: 'Linha', icon: '📈', description: 'Gráfico de linha temporal' },
    { id: 'pie', label: 'Pizza', icon: '🥧', description: 'Gráfico de pizza distributivo' },
    { id: 'cards', label: 'Cards', icon: '🃏', description: 'Cards de resumo' },
  ];

  return (
    <div className="space-y-6">
      {/* Visualization Type */}
      <div className="space-y-3">
        <Label className="text-base font-semibold">Tipo de Visualização</Label>
        <p className="text-sm text-muted-foreground">Escolha como seus dados serão exibidos</p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {visualizationOptions.map((viz) => (
            <button
              key={viz.id}
              type="button"
              onClick={() => updateConfig({ visualizationType: viz.id as any })}
              className={`p-4 border rounded-lg text-left transition-all hover:shadow-md ${
                config.visualizationType === viz.id
                  ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
                  : 'border-border hover:bg-muted/50'
              }`}
            >
              <div className="text-2xl mb-2">{viz.icon}</div>
              <div className="font-medium text-sm">{viz.label}</div>
              <div className="text-xs text-muted-foreground mt-1">{viz.description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Chart Options */}
      {config.visualizationType !== 'table' && config.visualizationType !== 'cards' && (
        <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
          <Label className="text-base font-semibold">Opções do Gráfico</Label>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Mostrar Legenda</div>
              <div className="text-sm text-muted-foreground">Exibir legenda dos dados</div>
            </div>
            <Switch
              checked={config.chartConfig?.showLegend ?? true}
              onCheckedChange={(checked) =>
                updateConfig({
                  chartConfig: { ...config.chartConfig, showLegend: checked }
                })
              }
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <div className="font-medium">Mostrar Valores</div>
              <div className="text-sm text-muted-foreground">Exibir rótulos nos dados</div>
            </div>
            <Switch
              checked={config.chartConfig?.showDataLabels ?? true}
              onCheckedChange={(checked) =>
                updateConfig({
                  chartConfig: { ...config.chartConfig, showDataLabels: checked }
                })
              }
            />
          </div>
        </div>
      )}

      {/* Report Name */}
      <div className="space-y-2">
        <Label htmlFor="report-name" className="text-base font-semibold">
          Nome do Relatório <span className="text-destructive">*</span>
        </Label>
        <Input
          id="report-name"
          placeholder="Ex: Fluxo de Caixa Mensal"
          value={reportName}
          onChange={(e) => updateState({ reportName: e.target.value })}
          maxLength={255}
        />
      </div>

      {/* Report Description */}
      <div className="space-y-2">
        <Label htmlFor="report-description" className="text-base font-semibold">
          Descrição (opcional)
        </Label>
        <Textarea
          id="report-description"
          placeholder="Descreva o propósito deste relatório..."
          value={reportDescription}
          onChange={(e) => updateState({ reportDescription: e.target.value })}
          rows={3}
          maxLength={1000}
        />
        <p className="text-xs text-muted-foreground text-right">
          {reportDescription.length}/1000
        </p>
      </div>

      {/* Options */}
      <div className="space-y-4 p-4 border rounded-lg bg-muted/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Star className={`h-5 w-5 ${isFavorite ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`} />
            <div>
              <div className="font-medium">Adicionar aos Favoritos</div>
              <div className="text-sm text-muted-foreground">Marque para acesso rápido</div>
            </div>
          </div>
          <Switch
            checked={isFavorite}
            onCheckedChange={(checked) => updateState({ isFavorite: checked })}
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="font-medium">Relatório Público</div>
            <div className="text-sm text-muted-foreground">Visível para todos os usuários da empresa</div>
          </div>
          <Switch
            checked={isPublic}
            onCheckedChange={(checked) => updateState({ isPublic: checked })}
          />
        </div>
      </div>
    </div>
  );
}
